import { useMutation } from "@tanstack/react-query";
import { captureException } from "@sentry/react";
import { isAxiosError } from "axios";
import { apiClient, signApiRequest } from "./";
import type { CustomerCredentials } from "../ui/customerDataStore";
import type { CachedCalculation } from "./useAppAuth";

interface LeadEngagement {
  ctaClickedAt?: string;
  insurelyInitiatedAt?: string;
}

// Toggle selections sent by the app (request side). The stored/received shape is the
// timestamp-based MarketingPreferences in useAppAuth.
export interface MarketingPreferencesInput {
  futureOffers?: boolean;
  newsletter?: boolean;
}

export interface PatchLeadPayload {
  email?: string | null;
  phone?: string;
  marketingPreferences?: MarketingPreferencesInput;
  appData?: {
    engagement?: LeadEngagement;
    cachedCalculation?: Partial<CachedCalculation>;
    // Topics the user picked in the contact form, for the advisor to prepare the meeting.
    contactTopics?: string[];
  };
}

const patchLead = async (
  leadId: string,
  payload: PatchLeadPayload,
  credentials: CustomerCredentials,
): Promise<void> => {
  const signedRequest = await signApiRequest(
    "PATCH",
    // deferOnMissing: the lead is created asynchronously after app auth, so the backend
    // queues this update for retry instead of 404-ing if the row does not exist yet.
    `${import.meta.env.VITE_API_URL}/user/lead/${encodeURIComponent(leadId)}?deferOnMissing=true`,
    credentials,
    { "content-type": "application/json" },
    payload as Record<string, unknown>,
  );
  await apiClient(signedRequest);
};

export const usePatchLead = (credentials: CustomerCredentials) =>
  useMutation<void, Error, { leadId: string; payload: PatchLeadPayload }>({
    mutationFn: ({ leadId, payload }) =>
      patchLead(leadId, payload, credentials),
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    // Persisting lead data is best-effort: a preview without stored data is acceptable.
    // Callers use mutate (never throws), so the only handling is to report the final failure to Sentry
    onError: (error, variables) => {
      // The raw axios error carries the request body (email/phone) in error.config.data,
      // which must not reach Sentry. Report a sanitized error correlated only by lead id / fid.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      captureException(
        new Error(`patchLead failed${status ? ` (status ${status})` : ""}`),
        {
          tags: {
            operation: "patchLead",
            errorType: error.name || "UnknownError",
          },
          extra: {
            leadId: variables.leadId,
            fid: credentials.identityId,
            status,
          },
          level: "error",
        },
      );
    },
  });
